'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Container } from 'components/shared';
import { useReviews } from 'components/ring-air/useReviews';
import { ReviewCard } from 'components/ring-air/ReviewCard';
import styles from './reviews-client.module.css';

const RingReviews = () => {
  const t = useTranslations('common');
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { reviews, loading, error, hasMore, loadMore } = useReviews({
    limit: 24,
    page: 1,
    requireContent: true,
  });

  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY || document.documentElement.scrollTop;
    setShowScrollTop(scrolled > 300);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const observerRef = loadMoreRef.current;
    if (!observerRef || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      {
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    observer.observe(observerRef);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, loadMore]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <main className={styles.root}>
      <Container className={styles.reviewsContainer}>
        <div className={styles.reviewsHeader}>
          <h1 className={styles.reviewsTitle}>{t('reviewsPage.heading')}</h1>
        </div>

        {loading && reviews.length === 0 && (
          <p className={styles.reviewsPlaceholder}>
            {t('reviewsSection.text.loadingReviews')}
          </p>
        )}

        {error && <p className={styles.reviewsPlaceholder}>{error}</p>}

        {!loading && !error && reviews.length === 0 && (
          <p className={styles.reviewsPlaceholder}>
            {t('reviewsSection.text.noReviewsAvailable')}
          </p>
        )}

        {reviews.length > 0 && (
          <div className={styles.reviewsBento}>
            {reviews.map((review) => (
              <ReviewCard key={review.review_id} review={review} />
            ))}
          </div>
        )}

        {hasMore && reviews.length > 0 && (
          <div ref={loadMoreRef} className={styles.loadMoreTrigger}>
            {loading && <div className={styles.loader} />}
          </div>
        )}

        {!hasMore && reviews.length > 0 && (
          <p className={styles.reviewsPlaceholder}>
            {t('reviewsPage.text.allReviewsLoaded')}
          </p>
        )}
      </Container>
      <button
        type="button"
        className={styles.scrollTopButton}
        onClick={scrollToTop}
        aria-label={t('reviewsPage.text.scrollToTop')}
        aria-describedby=""
        aria-hidden={!showScrollTop}
        tabIndex={showScrollTop ? 0 : -1}
        data-visible={showScrollTop}
      >
        ↑
      </button>
    </main>
  );
};

export default RingReviews;
